/**
 * Wishlist functionality for product cards
 */

class Wishlist {
  constructor() {
    this.storageKey = 'shopify_wishlist';
    this.items = this.loadWishlist();
    this.init();
  }

  init() {
    // Initialize all wishlist buttons
    this.updateAllButtons();
    
    // Add event listeners to all wishlist buttons
    document.addEventListener('click', (e) => {
      const button = e.target.closest('[data-wishlist-button]');
      if (button) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleWishlist(button);
      }
    });

    // Listen for storage changes (for multi-tab sync)
    window.addEventListener('storage', (e) => {
      if (e.key === this.storageKey) {
        this.items = this.loadWishlist();
        this.updateAllButtons();
      }
    });
  }

  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Error loading wishlist:', e);
      return [];
    }
  }

  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.items));
      
      // Dispatch custom event for other components
      window.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: { items: this.items }
      }));
    } catch (e) {
      console.error('Error saving wishlist:', e);
    }
  }

  isInWishlist(productId) {
    return this.items.some(item => item.id === productId);
  }

  addToWishlist(productId, productHandle) {
    if (!this.isInWishlist(productId)) {
      this.items.push({
        id: productId,
        handle: productHandle,
        addedAt: Date.now()
      });
      this.saveWishlist();
      return true;
    }
    return false;
  }

  removeFromWishlist(productId) {
    const initialLength = this.items.length;
    this.items = this.items.filter(item => item.id !== productId);
    
    if (this.items.length !== initialLength) {
      this.saveWishlist();
      return true;
    }
    return false;
  }

  toggleWishlist(button) {
    const productId = button.getAttribute('data-product-id');
    const productHandle = button.getAttribute('data-product-handle');

    if (!productId) return;

    const isActive = button.classList.contains('is-active');

    if (isActive) {
      // Remove from wishlist
      if (this.removeFromWishlist(productId)) {
        button.classList.remove('is-active');
        button.setAttribute('aria-label', button.getAttribute('data-label-add') || 'Add to wishlist');
        
        // Show notification (optional)
        this.showNotification('Removed from wishlist');
      }
    } else {
      // Add to wishlist
      if (this.addToWishlist(productId, productHandle)) {
        button.classList.add('is-active');
        button.setAttribute('aria-label', button.getAttribute('data-label-remove') || 'Remove from wishlist');
        
        // Show notification (optional)
        this.showNotification('Added to wishlist');
      }
    }
  }

  updateAllButtons() {
    const buttons = document.querySelectorAll('[data-wishlist-button]');
    buttons.forEach(button => {
      const productId = button.getAttribute('data-product-id');
      if (productId && this.isInWishlist(productId)) {
        button.classList.add('is-active');
      } else {
        button.classList.remove('is-active');
      }
    });
  }

  showNotification(message) {
    // Simple notification - you can customize this
    console.log(message);
    
    // Optional: Create a toast notification
    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: var(--color-text, #000);
      color: var(--color-background, #fff);
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 9999;
      font-size: 14px;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    // Fade in
    setTimeout(() => {
      notification.style.opacity = '1';
    }, 10);
    
    // Fade out and remove
    setTimeout(() => {
      notification.style.opacity = '0';
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 2000);
  }

  getWishlistItems() {
    return this.items;
  }

  getWishlistCount() {
    return this.items.length;
  }

  clearWishlist() {
    this.items = [];
    this.saveWishlist();
    this.updateAllButtons();
  }
}

// Initialize wishlist when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.shopifyWishlist = new Wishlist();
  });
} else {
  window.shopifyWishlist = new Wishlist();
}

